import React, { useState, useEffect, useMemo } from 'react';
import { dashboardAPI, casesAPI, commissionAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  PoundSterling, TrendingUp, Calendar, AlertTriangle, ArrowDownCircle, Clock,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { toast } from 'sonner';

const formatStatus = (s) => s?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-';

const COMMISSION_STATUSES = [
  { key: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { key: 'submitted_to_lender', label: 'Submitted', color: 'bg-blue-100 text-blue-800' },
  { key: 'paid', label: 'Paid', color: 'bg-green-100 text-green-800' },
  { key: 'clawed_back', label: 'Clawed Back', color: 'bg-red-100 text-red-800' },
];

const Commission = () => {
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('monthly');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, summaryData, monthlyData, casesData] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getForecast(),
        commissionAPI.getMonthly({}),
        casesAPI.getAll({}),
      ]);
      setStats(statsData);
      setSummary(summaryData);
      setMonthly(monthlyData);
      setCases(casesData.cases || []);
    } catch (err) {
      console.error('Failed to load:', err);
      toast.error('Failed to load commission data');
    } finally {
      setLoading(false);
    }
  };

  const loadMonthly = async (params = {}) => {
    try {
      const data = await commissionAPI.getMonthly(params);
      setMonthly(data);
    } catch (err) {
      toast.error('Failed to load monthly data');
    }
  };

  const handleViewChange = (mode) => {
    setViewMode(mode);
    if (mode === 'monthly') loadMonthly({});
    else if (mode === 'ytd') loadMonthly({ year: new Date().getFullYear() });
  };

  const handleCustomRange = () => {
    if (customRange.start && customRange.end) {
      loadMonthly({ start_date: customRange.start, end_date: customRange.end });
    }
  };

  const handleUpdateCommission = async (caseId, updates) => {
    try {
      await casesAPI.update(caseId, updates);
      toast.success('Commission updated');
      loadData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const parts = d.split('T')[0].split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return d;
  };

  const formatCurrency = (v) => {
    if (!v && v !== 0) return '£0';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(v);
  };

  const getFilteredCases = useMemo(() => {
    if (statusFilter === 'all') return cases.filter(c => c.gross_commission > 0 || c.proc_fee_total > 0 || c.client_fee > 0);
    return cases.filter(c => c.commission_status === statusFilter && (c.gross_commission > 0 || c.proc_fee_total > 0 || c.client_fee > 0));
  }, [cases, statusFilter]);

  const getStatusBadge = (status) => {
    const found = COMMISSION_STATUSES.find(s => s.key === status);
    return found ? found.color : 'bg-slate-100 text-slate-800';
  };

  const monthlyChartData = useMemo(() => monthly?.monthly?.map(m => ({
    month: m.month,
    Pending: m.pending,
    Submitted: m.submitted,
    Paid: m.paid,
    'Clawed Back': m.clawed_back,
  })) || [], [monthly]);

  const monthlyBreakdownData = useMemo(() => monthly?.monthly?.map(m => ({
    month: m.month,
    Mortgage: m.mortgage_commission,
    Insurance: m.insurance_commission,
    'Proc Fees': m.proc_fees,
    Total: m.total_commission,
  })) || [], [monthly]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-red-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="commission-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>Commission & Revenue</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Track your earnings and revenue</p>
      </div>

      {/* Summary Cards - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 stat-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Paid Commission</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white mt-1" data-testid="total-paid-commission">{formatCurrency(stats?.total_commission)}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center"><PoundSterling className="h-6 w-6 text-green-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 stat-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Proc Fees</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white mt-1" data-testid="total-proc-fees">{formatCurrency(summary?.total_proc_fees || stats?.total_proc_fees)}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center"><TrendingUp className="h-6 w-6 text-blue-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 stat-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Pending Commission</p>
                <p className="text-3xl font-bold text-yellow-700 mt-1" data-testid="pending-commission">{formatCurrency(summary?.total_pending_commission)}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center"><AlertTriangle className="h-6 w-6 text-yellow-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 stat-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Clawed Back</p>
                <p className="text-3xl font-bold text-red-600 mt-1" data-testid="clawed-back">{formatCurrency(summary?.total_clawed_back)}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center"><ArrowDownCircle className="h-6 w-6 text-red-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commission Paid This Month + Last Month */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200 bg-gradient-to-br from-green-50 dark:from-green-900/20 to-white dark:to-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="h-5 w-5 text-green-600" />
              <p className="text-sm font-medium text-green-700">Commission Paid This Month</p>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white" data-testid="commission-this-month">{formatCurrency(summary?.commission_this_month?.amount)}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{summary?.commission_this_month?.cases || 0} cases &middot; Proc Fees: {formatCurrency(summary?.commission_this_month?.proc_fees)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-blue-50 dark:from-blue-900/20 to-white dark:to-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="h-5 w-5 text-blue-600" />
              <p className="text-sm font-medium text-blue-700">Commission Paid Last Month</p>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white" data-testid="commission-last-30">{formatCurrency(summary?.commission_last_month?.amount)}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{summary?.commission_last_month?.cases || 0} cases &middot; Proc Fees: {formatCurrency(summary?.commission_last_month?.proc_fees)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Client Fees Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 stat-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Client Fees (Paid)</p>
                <p className="text-3xl font-bold text-purple-700 mt-1" data-testid="total-client-fees">{formatCurrency(summary?.total_client_fees_paid)}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center"><PoundSterling className="h-6 w-6 text-purple-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 stat-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Client Fee Pending</p>
                <p className="text-3xl font-bold text-yellow-700 mt-1" data-testid="client-fee-pending">{formatCurrency(summary?.client_fee_pending)}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center"><AlertTriangle className="h-6 w-6 text-yellow-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 stat-card bg-gradient-to-br from-slate-50 dark:from-slate-900 to-white dark:to-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Commission + Client Fees</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white mt-1" data-testid="total-commission-and-fees">{formatCurrency((stats?.total_commission || 0) + (summary?.total_client_fees_paid || 0))}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-slate-200 flex items-center justify-center"><TrendingUp className="h-6 w-6 text-slate-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-purple-50 dark:from-purple-900/20 to-white dark:to-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              <p className="text-sm font-medium text-purple-700">Client Fees Paid This Month</p>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white" data-testid="client-fees-this-month">{formatCurrency(summary?.client_fees_paid_this_month)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-indigo-50 dark:from-indigo-900/20 to-white dark:to-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="h-5 w-5 text-indigo-600" />
              <p className="text-sm font-medium text-indigo-700">Client Fees Paid Last Month</p>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white dark:text-white" data-testid="client-fees-last-30">{formatCurrency(summary?.client_fees_paid_last_month)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Mortgage vs Insurance Split */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 dark:text-slate-400">Mortgage Commission</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{formatCurrency(monthly?.totals?.total_mortgage || stats?.mortgage_commission)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 dark:text-slate-400">Insurance Commission</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{formatCurrency(monthly?.totals?.total_insurance || stats?.insurance_commission)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 dark:text-slate-400">Grand Total (All)</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{formatCurrency(monthly?.totals?.grand_total)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Revenue View */}
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Monthly Commission Breakdown</CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex border rounded-lg overflow-hidden">
              {['monthly', 'ytd', 'custom'].map((mode) => (
                <button key={mode} onClick={() => handleViewChange(mode)} className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === mode ? 'bg-red-600 text-white' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600'}`} data-testid={`view-${mode}-btn`}>
                  {mode === 'monthly' ? 'Monthly' : mode === 'ytd' ? 'Year to Date' : 'Custom Range'}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'custom' && (
            <div className="flex gap-3 mb-4 items-end">
              <div className="space-y-1"><Label className="text-xs">Start Date</Label><Input type="date" value={customRange.start} onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })} className="w-40" /></div>
              <div className="space-y-1"><Label className="text-xs">End Date</Label><Input type="date" value={customRange.end} onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })} className="w-40" /></div>
              <Button onClick={handleCustomRange} size="sm" className="bg-red-600 hover:bg-red-700">Apply</Button>
            </div>
          )}
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `£${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Legend />
                <Bar dataKey="Pending" stackId="a" fill="#EAB308" />
                <Bar dataKey="Submitted" stackId="a" fill="#3B82F6" />
                <Bar dataKey="Paid" stackId="a" fill="#22C55E" />
                <Bar dataKey="Clawed Back" stackId="a" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Revenue by Type Chart */}
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
        <CardHeader><CardTitle>Monthly Revenue: Mortgage vs Insurance vs Proc Fees</CardTitle></CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={monthlyBreakdownData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `£${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Legend />
                <Bar dataKey="Mortgage" fill="#DC2626" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Insurance" fill="#2563EB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Proc Fees" fill="#16A34A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Paid Commission — grouped by month, newest first */}
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
        <CardHeader>
          <CardTitle>Paid Commission History</CardTitle>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">All paid commissions grouped by month, newest first</p>
        </CardHeader>
        <CardContent className="p-0">
          {(() => {
            // Filter to paid cases only, sort by paid date newest first
            const paidCases = cases
              .filter(c => c.commission_status === 'paid' && c.gross_commission > 0)
              .sort((a, b) => {
                const dateA = a.commission_paid_date || a.expected_completion_date || a.created_at || '';
                const dateB = b.commission_paid_date || b.expected_completion_date || b.created_at || '';
                return dateB.localeCompare(dateA);
              });

            // Build combined list of commission + client fee entries
            const allEntries = [];
            cases.forEach(c => {
              // Commission entry
              if (c.commission_status === 'paid' && c.gross_commission > 0) {
                allEntries.push({
                  ...c,
                  _type: 'commission',
                  _amount: c.gross_commission,
                  _date: c.commission_paid_date || c.expected_completion_date || c.created_at || '',
                });
              }
              // Client fee entry (separate row if paid)
              if (c.client_fee_status === 'paid' && c.client_fee > 0) {
                allEntries.push({
                  ...c,
                  _type: 'client_fee',
                  _amount: c.client_fee,
                  _date: c.client_fee_paid_date || c.expected_completion_date || c.created_at || '',
                });
              }
            });

            // Sort all entries newest first
            allEntries.sort((a, b) => b._date.localeCompare(a._date));

            if (allEntries.length === 0) {
              return (
                <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                  <PoundSterling className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p>No paid commissions yet</p>
                </div>
              );
            }

            // Group by month
            const grouped = {};
            allEntries.forEach(entry => {
              const month = entry._date ? entry._date.slice(0, 7) : 'Unknown';
              if (!grouped[month]) grouped[month] = [];
              grouped[month].push(entry);
            });

            // Sort months newest first
            const sortedMonths = Object.keys(grouped).sort((a, b) => {
              if (a === 'Unknown') return 1;
              if (b === 'Unknown') return -1;
              return b.localeCompare(a);
            });

            const formatMonth = (key) => {
              if (key === 'Unknown') return 'Unknown Date';
              try {
                const [year, month] = key.split('-');
                return new Date(year, month - 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
              } catch { return key; }
            };

            const grandTotal = allEntries.reduce((sum, e) => sum + (e._amount || 0), 0);

            return (
              <div>
                {sortedMonths.map((month) => {
                  const monthEntries = grouped[month];
                  const monthTotal = monthEntries.reduce((sum, e) => sum + (e._amount || 0), 0);
                  return (
                    <div key={month}>
                      {/* Month header */}
                      <div className="flex items-center justify-between px-6 py-3 bg-slate-50 dark:bg-slate-900/50 border-y border-slate-200 dark:border-slate-700">
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{formatMonth(month)}</span>
                        <span className="text-sm font-bold text-green-700 dark:text-green-400">{formatCurrency(monthTotal)}</span>
                      </div>
                      {/* Entries in this month */}
                      <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                        {monthEntries.map((entry, idx) => {
                          const isClientFee = entry._type === 'client_fee';
                          return (
                            <div key={`${entry.case_id}-${entry._type}`} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                              <div className="flex items-center gap-4 min-w-0">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isClientFee ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-green-100 dark:bg-green-900/40'}`}>
                                  <PoundSterling className={`h-4 w-4 ${isClientFee ? 'text-purple-700 dark:text-purple-400' : 'text-green-700 dark:text-green-400'}`} />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-semibold text-slate-900 dark:text-white text-sm truncate">{entry.client_name || 'Unknown Client'}</p>
                                    {isClientFee && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium flex-shrink-0">Client Fee</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">{(entry.product_type || '').replace('_', ' ')}</span>
                                    {entry.mortgage_type && <span className="text-xs text-slate-400 dark:text-slate-500">· {(entry.mortgage_type || '').replace(/_/g, ' ')}</span>}
                                    {entry.lender_name && <span className="text-xs text-slate-400 dark:text-slate-500">· {entry.lender_name}</span>}
                                    {entry.insurance_provider && <span className="text-xs text-slate-400 dark:text-slate-500">· {entry.insurance_provider}</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-6 flex-shrink-0 ml-4">
                                {!isClientFee && (
                                  <div className="text-right hidden sm:block">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Proc Fee</p>
                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{formatCurrency(entry.proc_fee_total)}</p>
                                  </div>
                                )}
                                <div className="text-right hidden md:block">
                                  <p className="text-xs text-slate-500 dark:text-slate-400">Date Paid</p>
                                  <p className="text-sm text-slate-600 dark:text-slate-300">
                                    {entry._date ? new Date(entry._date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-slate-500 dark:text-slate-400">{isClientFee ? 'Client Fee' : 'Commission'}</p>
                                  <p className={`text-base font-bold ${isClientFee ? 'text-purple-700 dark:text-purple-400' : 'text-green-700 dark:text-green-400'}`}>
                                    {formatCurrency(entry._amount)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {/* Grand total */}
                <div className="flex items-center justify-between px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t-2 border-slate-200 dark:border-slate-700">
                  <span className="font-bold text-slate-900 dark:text-white">Total Paid (Commission + Client Fees)</span>
                  <span className="text-lg font-bold text-green-700 dark:text-green-400">{formatCurrency(grandTotal)}</span>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Commission Tracker */}
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Commission Tracker</CardTitle>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="commission-status-filter"><SelectValue placeholder="Filter by status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {COMMISSION_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {getFilteredCases.length === 0 ? (
            <div className="text-center py-12">
              <PoundSterling className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No commissions found</h3>
              <p className="text-slate-500">Cases with commission will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {getFilteredCases.map((c) => (
                <div key={c.case_id} className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors" data-testid={`commission-row-${c.case_id}`}>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">{c.client_name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {c.lender_name || c.insurance_provider || 'No provider'} &middot; {formatStatus(c.product_type)}
                      {c.commission_paid_date && <span className="ml-2 text-green-600">Paid: {formatDate(c.commission_paid_date)}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-slate-900 dark:text-white">{formatCurrency(c.gross_commission)}</p>
                      {c.proc_fee_total > 0 && <p className="text-xs text-slate-500">Proc Fee: {formatCurrency(c.proc_fee_total)}</p>}
                      {c.client_fee > 0 && <p className="text-xs text-purple-600">Client Fee: {formatCurrency(c.client_fee)}</p>}
                    </div>
                    <Select value={c.commission_status} onValueChange={(v) => handleUpdateCommission(c.case_id, { commission_status: v })}>
                      <SelectTrigger className="w-[150px]">
                        <Badge className={getStatusBadge(c.commission_status)}>
                          {COMMISSION_STATUSES.find(s => s.key === c.commission_status)?.label || c.commission_status}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>{COMMISSION_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Commission;
